import { getCsrfToken } from '../auth/authApi'
import type { MaterialSnapshot } from './types'
import type { PickerPage } from '../../shared/components/EntityPicker'
import { getDeletionAvailability } from '../../shared/deletion/deletionApi'
import { apiFetch } from '../../shared/http/apiFetch'
const baseUrl='/api/v1/materials'
type MaterialQueryOptions={section?:string;query?:string;status?:string;page?:number;pageSize?:number;cacheScope?:string|null}
const snapshotCache=new Map<string,{storedAt:number;value:MaterialSnapshot}>()
const cacheLifetimeMs=5*60*1000
function requestUrl(path:string,options?:MaterialQueryOptions){
  const parameters=new URLSearchParams()
  if(options?.section)parameters.set('section',options.section)
  if(options?.query)parameters.set('query',options.query)
  if(options?.status)parameters.set('status',options.status)
  if(options?.page)parameters.set('page',String(options.page))
  if(options?.pageSize)parameters.set('pageSize',String(options.pageSize))
  return `${baseUrl}${path}${parameters.size?`?${parameters}`:''}`
}
function cacheKey(url:string,scope?:string|null){return `${scope??'anonymous'}:${url}`}
function peek(url:string,scope?:string|null){
  const cached=snapshotCache.get(cacheKey(url,scope))
  if(!cached||Date.now()-cached.storedAt>cacheLifetimeMs){if(cached)snapshotCache.delete(cacheKey(url,scope));return null}
  return cached.value
}
async function fetchSnapshot(url:string,scope?:string|null){
  const response=await apiFetch(url,{credentials:'include'})
  if(!response.ok){const payload=await response.json().catch(()=>null) as {code?:string}|null;throw new Error(payload?.code??'materials.loadError')}
  const value=await response.json() as MaterialSnapshot
  snapshotCache.set(cacheKey(url,scope),{storedAt:Date.now(),value})
  return value
}
export async function getMaterials(options?:MaterialQueryOptions):Promise<MaterialSnapshot>{
  return fetchSnapshot(requestUrl('',options),options?.cacheScope)
}
export function peekMaterials(options?:MaterialQueryOptions){
  const url=requestUrl('',options);return peek(url,options?.cacheScope)
}
async function getMaterialPath(path:string,options?:MaterialQueryOptions){return fetchSnapshot(requestUrl(path,options),options?.cacheScope)}
export const getArticleMaterials=(id:string,cacheScope?:string|null)=>getMaterialPath(`/articles/${id}`,{cacheScope})
export const peekArticleMaterials=(id:string,cacheScope?:string|null)=>peek(requestUrl(`/articles/${id}`),cacheScope)
export async function getMaterialArticleOptions(query:string,page:number,tracking?:'lot'|'serial',selectedId?:string):Promise<PickerPage>{
  const parameters=new URLSearchParams({page:String(page)})
  if(query)parameters.set('query',query)
  if(tracking)parameters.set('tracking',tracking)
  if(selectedId)parameters.set('selectedId',selectedId)
  const response=await apiFetch(`${baseUrl}/article-options?${parameters}`,{credentials:'include'})
  if(!response.ok)throw new Error('materials.loadError')
  return response.json() as Promise<PickerPage>
}
export const getTrackingMaterials=(kind:'lot'|'serial',id?:string,options?:MaterialQueryOptions)=>
  getMaterialPath(`/${kind==='lot'?'lots':'serial-numbers'}${id?`/${id}`:''}`,options)
export const peekTrackingMaterials=(kind:'lot'|'serial',id?:string,options?:MaterialQueryOptions)=>
  peek(requestUrl(`/${kind==='lot'?'lots':'serial-numbers'}${id?`/${id}`:''}`,options),options?.cacheScope)
export async function materialCommand(path:string,method:'POST'|'PUT'|'DELETE',body?:unknown){
  const response=await apiFetch(`${baseUrl}${path}`,{method,credentials:'include',
    headers:{'Content-Type':'application/json','X-CSRF-TOKEN':await getCsrfToken()},
    ...(body===undefined?{}:{body:JSON.stringify(body)})})
  const payload=await response.json().catch(()=>null) as {code?:string;planningScenarioRequired?:boolean}|null
  if(!response.ok) throw new Error(payload?.code??'materials.actionError')
  snapshotCache.clear()
  if(payload?.planningScenarioRequired)
    window.dispatchEvent(new CustomEvent('apl:planning-scenario-required'))
  return payload?.code??'materials.saved'
}
export const getMaterialDeletionAvailability=(path:string)=>getDeletionAvailability(`${baseUrl}${path}`)
